import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUIStore, useWorkspaceStore } from '../../store';
import { api } from '../../lib/api';
import {
  LayoutDashboard,
  CheckSquare,
  AlertTriangle,
  MessageSquare,
  BarChart3,
  Settings,
  FileText,
  Sun,
  Moon,
  Rows3,
  ChevronDown,
  Check
} from 'lucide-react';

const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const { sidebarOpen, toggleSidebar, theme, setTheme, language, setLanguage, density, setDensity } = useUIStore();
  // Header.tsx already fetches+populates this on mount — read only here,
  // no second fetch. Product name/logo stay fixed in Header; only the
  // workspace name lives in the sidebar (its own, standalone, prominent).
  // myWorkspaces powers the switcher dropdown when the caller is a member
  // of more than one workspace; switchWorkspace already does the reload
  // itself, so we just await it.
  const { workspace, myWorkspaces, switchWorkspace } = useWorkspaceStore();
  const location = useLocation();

  const [counts, setCounts] = useState<{
    tasksBadge: number;
    incidentsBadge: number;
    savedViews: { assignedToMe: number; unclaimed: number; breachingSla: number };
  } | null>(null);

  // On phones/tablets the sidebar is an overlay drawer: close it after navigating
  // so the page you just opened isn't hidden behind it.
  useEffect(() => {
    const ui = useUIStore.getState();
    if (window.innerWidth < 1024 && ui.sidebarOpen) ui.toggleSidebar();
  }, [location.pathname]);

  useEffect(() => {
    api.get('/dashboard/sidebar-counts')
      .then((res) => setCounts((res.data as any)?.data || null))
      .catch(() => {});
  }, []);
  // Dropdown is closed by default. Only meaningful when myWorkspaces has
  // more than one entry — the static single-workspace view below stays
  // pixel-identical to the pre-feature design (no chevron, no button).
  const [showWorkspaceMenu, setShowWorkspaceMenu] = React.useState(false);

  const navigationOperate = [
    { name: t('common.dashboard'), href: '/dashboard', icon: LayoutDashboard },
    { name: t('common.tasks'), href: '/tasks', icon: CheckSquare },
    { name: t('common.incidents'), href: '/incidents', icon: AlertTriangle },
    { name: t('common.messages'), href: '/messages', icon: MessageSquare },
  ];

  const navigationManage = [
    { name: t('common.reports'), href: '/reports', icon: BarChart3 },
    { name: t('common.documents'), href: '/documents', icon: FileText },
    { name: t('common.settings'), href: '/settings', icon: Settings },
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'tr', name: 'Türkçe' },
    { code: 'es', name: 'Español' },
  ];



  return (
    <>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed top-16 inset-x-0 bottom-0 z-40 lg:hidden bg-gray-600 bg-opacity-75"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-16 left-0 z-50 w-[216px] ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Workspace name — its own, standalone block at the top of the
              sidebar (product name/logo live in Header.tsx instead). When
              the caller is a member of more than one workspace, the block
              becomes a button with a chevron and reveals a switcher
              dropdown just below it. Single-workspace accounts see the
              exact same static block they always did (no chevron, not
              clickable) — the visual is intentionally pixel-identical for
              the 99% case. */}
          {workspace?.name && myWorkspaces.length <= 1 && (
            <div className={`h-16 px-[14px] flex items-center border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <p className={`text-sm font-semibold truncate ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{workspace.name}</p>
            </div>
          )}
          {workspace?.name && myWorkspaces.length > 1 && (
            <div className={`relative border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                type="button"
                onClick={() => setShowWorkspaceMenu(v => !v)}
                className={`w-full h-16 px-[14px] flex items-center justify-between transition-colors duration-200 ${
                  theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
              >
                <p className={`text-sm font-semibold truncate ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{workspace.name}</p>
                <ChevronDown className={`h-4 w-4 flex-shrink-0 ml-2 transition-transform duration-200 ${showWorkspaceMenu ? 'rotate-180' : ''} ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              </button>
              {showWorkspaceMenu && (
                <div
                  className={`absolute left-0 right-0 top-full z-10 max-h-[280px] overflow-y-auto shadow-lg border ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}
                >
                  <p className={`px-[14px] pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.1em] ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                    {t('sidebar.switchWorkspace')}
                  </p>
                  {myWorkspaces.map((m) => {
                    const isActive = m.is_active;
                    const roleKey = m.role === 'client_user' ? 'clientUser' : m.role;
                    return (
                      <button
                        key={m.workspace_id}
                        type="button"
                        disabled={isActive}
                        onClick={async () => {
                          if (isActive) return;
                          setShowWorkspaceMenu(false);
                          await switchWorkspace(m.workspace_id);
                        }}
                        className={`w-full px-[14px] py-2 flex items-center justify-between gap-2 text-left transition-colors duration-200 ${
                          isActive
                            ? `cursor-default ${theme === 'dark' ? 'bg-gray-700/60' : 'bg-gray-50'}`
                            : theme === 'dark'
                              ? 'hover:bg-gray-700'
                              : 'hover:bg-gray-100'
                        }`}
                      >
                        <span className="flex flex-col min-w-0">
                          <span className={`text-[12.5px] truncate ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{m.workspace_name}</span>
                          <span className={`text-[10px] mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{t(`settings.profile.roles.${roleKey}`)}</span>
                        </span>
                        {isActive && (
                          <Check className={`h-3.5 w-3.5 flex-shrink-0 ${theme === 'dark' ? 'text-primary-400' : 'text-primary-600'}`} />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Navigation — grouped into Operate / Manage per the ITIL
              Console mockup's sidebar sections (no Board/Changes pages
              added, existing 7 pages only — see docs/OPEN_ITEMS.md).
              Sizing/spacing matched to the mockup's own inline styles
              (216px sidebar, 12.5px nav text, 7px padding/radius, 10px
              uppercase section labels) — colors kept as-is. */}
          <nav className="flex-1 py-3 overflow-y-auto">
            <div>
              <p className={`px-[14px] pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                {t('sidebar.groups.operate')}
              </p>
              <div className="px-2 space-y-px">
                {navigationOperate.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;

                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={`flex items-center gap-[9px] px-[9px] py-[7px] text-[12.5px] font-medium rounded-[7px] transition-all duration-200 ${
                        isActive
                          ? 'bg-[#76d1e3] text-white shadow-sm'
                          : theme === 'dark'
                            ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <Icon className={`h-[14px] w-[14px] flex-shrink-0 ${isActive ? 'text-white' : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                      {item.name}
                      {item.href === '/tasks' && counts && counts.tasksBadge > 0 && (
                        <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/25 text-white' : theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                          {counts.tasksBadge}
                        </span>
                      )}
                      {item.href === '/incidents' && counts && counts.incidentsBadge > 0 && (
                        <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/25 text-white' : theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                          {counts.incidentsBadge}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>

            <div>
              <p className={`px-[14px] pt-3.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                {t('sidebar.groups.manage')}
              </p>
              <div className="px-2 space-y-px">
                {navigationManage.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;

                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={`flex items-center gap-[9px] px-[9px] py-[7px] text-[12.5px] font-medium rounded-[7px] transition-all duration-200 ${
                        isActive
                          ? 'bg-[#76d1e3] text-white shadow-sm'
                          : theme === 'dark'
                            ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <Icon className={`h-[14px] w-[14px] flex-shrink-0 ${isActive ? 'text-white' : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                      {item.name}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* Settings */}
          <div className={`border-t ${theme === 'dark' ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'} px-3 py-2.5 space-y-2`}>
            {/* Theme Toggle */}
            <div className="flex items-center justify-between">
              <span className={`text-[11.5px] ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{t('common.theme')}: {theme === 'light' ? t('common.light') : t('common.dark')}</span>
              <button
                onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                className={`p-1.5 rounded-lg transition-colors duration-200 border ${
                  theme === 'dark'
                    ? 'bg-gray-700 hover:bg-gray-600 border-gray-600 text-white'
                    : 'bg-white hover:bg-gray-100 border-gray-200 text-gray-700'
                }`}
                title={theme === 'light' ? t('common.dark') : t('common.light')}
              >
                {theme === 'light' ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />}
              </button>
            </div>

            {/* Language Selector */}
            <div className="flex items-center justify-between">
              <span className={`text-[11.5px] ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{t('common.language')}</span>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as 'en' | 'tr' | 'es')}
                className={`text-[11.5px] border rounded-lg px-1.5 py-1 ${
                  theme === 'dark'
                    ? 'border-gray-600 bg-gray-700 text-white'
                    : 'border-gray-300 bg-white text-gray-700'
                }`}
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Density Toggle (global compact/comfortable, same persist as theme).
                Only affects the Tasks/Incidents TABLES; below lg those pages show
                cards, which ignore it, so the control is hidden there. */}
            <div className="hidden lg:flex items-center justify-between">
              <span className={`text-[11.5px] ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{t('common.density')}: {density === 'comfortable' ? t('tasks.board.densityComfortable') : t('tasks.board.densityCompact')}</span>
              <button
                onClick={() => setDensity(density === 'comfortable' ? 'compact' : 'comfortable')}
                className={`p-1.5 rounded-lg transition-colors duration-200 border ${
                  theme === 'dark'
                    ? 'bg-gray-700 hover:bg-gray-600 border-gray-600 text-white'
                    : 'bg-white hover:bg-gray-100 border-gray-200 text-gray-700'
                }`}
                title={density === 'comfortable' ? t('tasks.board.densityCompact') : t('tasks.board.densityComfortable')}
              >
                <Rows3 className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Contact — same OquMail mailbox mentioned on the landing
                page footer, just surfaced here too since logged-in users
                never see the landing page. */}
            <div className={`flex items-center justify-between pt-1 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <span className={`text-[11.5px] ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{t('common.contact')}</span>
              <a
                href="mailto:info@ceben.net"
                className={`text-[11.5px] hover:underline ${theme === 'dark' ? 'text-primary-400' : 'text-primary-600'}`}
              >
                info@ceben.net
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
